import { InputBaseComponentProps } from "@mui/material/InputBase";
import type { ChangeEvent, ComponentProps } from "react";
import { forwardRef } from "react";
import { IMaskInput } from "react-imask";

import { DEFINITIONS } from "@/constants/mask.constants";
import { BRAZIL_PHONE_DYNAMIC_MASK, formatDigitsToPhoneMaskInput } from "@/helpers/phone-helpers";

type IMaskInputMask = ComponentProps<typeof IMaskInput>["mask"];

interface CustomProps extends Omit<InputBaseComponentProps, "children"> {
  mask: string;
  definitions?: Record<string, RegExp | string>;
}

export const TextMaskCustom = forwardRef<HTMLInputElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { mask, definitions, ...other } = props;

    const safeProps = {
      disabled: false,
      name: "",
      onChange: (() => {}) as (e: ChangeEvent<HTMLInputElement>) => void,
      onBlur: () => {},
      onFocus: () => {},
      ...other,
    };

    return (
      <IMaskInput
        {...safeProps}
        mask={mask}
        definitions={definitions}
        inputRef={ref}
        onAccept={(value: string) => {
          if (typeof safeProps.onChange === "function") {
            safeProps.onChange({
              target: {
                name: safeProps.name || "",
                value,
              },
            } as unknown as ChangeEvent<HTMLInputElement>);
          }
        }}
        overwrite
      />
    );
  }
);

type BrazilPhoneMaskProps = Omit<InputBaseComponentProps, "children">;

/** Máscara BR dinâmica: 9999-9999 (8 dígitos) ou 99999-9999 (9 dígitos). */
export const BrazilPhoneMaskInput = forwardRef<HTMLInputElement, BrazilPhoneMaskProps>(
  function BrazilPhoneMaskInput(props, ref) {
    const { value, onChange, name, ...other } = props;
    const digits = String(value ?? "").replace(/\D/g, "");
    const maskedValue = formatDigitsToPhoneMaskInput(digits);

    const safeProps = {
      disabled: false,
      name: name ?? "",
      onChange: (() => {}) as (e: ChangeEvent<HTMLInputElement>) => void,
      onBlur: () => {},
      onFocus: () => {},
      ...other,
    };

    const imaskProps = {
      ...safeProps,
      value: maskedValue,
      mask: BRAZIL_PHONE_DYNAMIC_MASK as unknown as IMaskInputMask,
      definitions: DEFINITIONS.PHONE,
      inputRef: ref,
      overwrite: true,
      onAccept: (acceptedValue: string) => {
        if (typeof onChange === "function") {
          onChange({
            target: {
              name: name ?? "",
              value: acceptedValue,
            },
          } as unknown as ChangeEvent<HTMLInputElement>);
        }
      },
    } as ComponentProps<typeof IMaskInput>;

    return <IMaskInput {...imaskProps} />;
  }
);
